'use client';

import { useEffect, useRef, useState } from 'react';

interface SmokeCanvasProps {
  color?: { r: number; g: number; b: number };
}

const useIsMobile = () => {
  const [isMobile, setIsMobile] = useState(false);
  
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 1024);
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);
  
  return isMobile;
};

export const SmokeCanvas = ({ color }: SmokeCanvasProps) => {
  const r = color?.r ?? 0;
  const g = color?.g ?? 212;
  const b = color?.b ?? 255;
  const smokeColor = { r, g, b };
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const isMobile = useIsMobile();

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const resizeCanvas = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };
    resizeCanvas();
    window.addEventListener('resize', resizeCanvas);

    let animationId: number;

    if (isMobile) {
      // Mobile: Random floating smoke dots
      class SmokeDot {
        x: number;
        y: number;
        vx: number;
        vy: number;
        size: number;
        opacity: number;
        pulseOffset: number;

        constructor() {
          this.x = Math.random() * canvas.width;
          this.y = Math.random() * canvas.height;
          this.vx = (Math.random() - 0.5) * 0.3;
          this.vy = (Math.random() - 0.5) * 0.3;
          this.size = 40 + Math.random() * 60;
          this.opacity = 0.03 + Math.random() * 0.05;
          this.pulseOffset = Math.random() * Math.PI * 2;
        }

        update() {
          this.x += this.vx;
          this.y += this.vy;

          // Wrap around edges
          if (this.x < -this.size) this.x = canvas.width + this.size;
          if (this.x > canvas.width + this.size) this.x = -this.size;
          if (this.y < -this.size) this.y = canvas.height + this.size;
          if (this.y > canvas.height + this.size) this.y = -this.size;
        }

        draw() {
          if (!ctx) return;

          const pulse = Math.sin(Date.now() * 0.001 + this.pulseOffset) * 0.3 + 1;
          const currentSize = this.size * pulse;

          const gradient = ctx.createRadialGradient(
            this.x, this.y, 0,
            this.x, this.y, currentSize
          );

          const { r, g, b } = smokeColor;
          gradient.addColorStop(0, `rgba(${r}, ${g}, ${b}, ${this.opacity * 0.6})`);
          gradient.addColorStop(0.3, `rgba(${r}, ${g}, ${b}, ${this.opacity * 0.3})`);
          gradient.addColorStop(0.6, `rgba(${r}, ${g}, ${b}, ${this.opacity * 0.1})`);
          gradient.addColorStop(1, 'rgba(0, 0, 0, 0)');

          ctx.fillStyle = gradient;
          ctx.globalAlpha = 0.7;
          ctx.beginPath();
          ctx.arc(this.x, this.y, currentSize, 0, Math.PI * 2);
          ctx.fill();
        }
      }

      const dots: SmokeDot[] = [];
      for (let i = 0; i < 8; i++) {
        dots.push(new SmokeDot());
      }

      const animateMobile = () => {
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        ctx.globalCompositeOperation = 'screen';

        dots.forEach(dot => {
          dot.update();
          dot.draw();
        });

        ctx.globalCompositeOperation = 'source-over';
        animationId = requestAnimationFrame(animateMobile);
      };

      animateMobile();

      return () => {
        window.removeEventListener('resize', resizeCanvas);
        cancelAnimationFrame(animationId);
      };
    }

    // Desktop: Smoke trail snake animation
    class SmokeTrail {
      segments: Array<{ x: number; y: number; vx: number; vy: number }>;
      baseOpacity: number;
      isFollowing: boolean;
      followDelay: number;
      index: number;

      constructor(index: number) {
        this.index = index;
        this.segments = [];
        const segmentCount = 15;

        for (let i = 0; i < segmentCount; i++) {
          this.segments.push({
            x: Math.random() * canvas.width,
            y: Math.random() * canvas.height,
            vx: (Math.random() - 0.5) * 0.5,
            vy: (Math.random() - 0.5) * 0.5
          });
        }

        this.baseOpacity = Math.random() * 0.08 + 0.04;
        this.isFollowing = false;
        this.followDelay = 0.08 + index * 0.008;
      }

      update(mouseX: number, mouseY: number) {
        const head = this.segments[0];
        const dx = mouseX - head.x;
        const dy = mouseY - head.y;
        const distance = Math.sqrt(dx * dx + dy * dy);

        if (distance < 300) {
          this.isFollowing = true;
        } else if (distance > 400) {
          this.isFollowing = false;
        }

        if (this.isFollowing) {
          head.x += (mouseX - head.x) * this.followDelay;
          head.y += (mouseY - head.y) * this.followDelay;
        } else {
          head.x += head.vx;
          head.y += head.vy;

          if (head.x < 0 || head.x > canvas.width) head.vx *= -1;
          if (head.y < 0 || head.y > canvas.height) head.vy *= -1;
        }

        for (let i = 1; i < this.segments.length; i++) {
          const current = this.segments[i];
          const previous = this.segments[i - 1];

          const segDx = previous.x - current.x;
          const segDy = previous.y - current.y;

          const delay = 0.15 - (i * 0.008);
          current.x += segDx * delay;
          current.y += segDy * delay;

          current.x += (Math.random() - 0.5) * 0.5;
          current.y += (Math.random() - 0.5) * 0.5;
        }
      }

      draw() {
        if (!ctx) return;

        for (let i = 0; i < this.segments.length; i++) {
          const segment = this.segments[i];

          const progress = i / this.segments.length;
          const sizeMultiplier = Math.sin(progress * Math.PI);
          const baseSize = 80 + Math.sin(Date.now() * 0.001 + i) * 20;
          const size = baseSize * (0.5 + sizeMultiplier * 0.8);

          const opacity = this.baseOpacity * (1 - progress * 0.5);

          const gradient = ctx.createRadialGradient(
            segment.x, segment.y, 0,
            segment.x, segment.y, size
          );

          const { r, g, b } = smokeColor;
          const fade = (factor: number) => `rgba(${Math.round(r * factor)}, ${Math.round(g * factor)}, ${Math.round(b * factor)}`;
          gradient.addColorStop(0, `${fade(1)}, ${opacity * 0.6})`);
          gradient.addColorStop(0.1, `${fade(0.94)}, ${opacity * 0.5})`);
          gradient.addColorStop(0.2, `${fade(0.85)}, ${opacity * 0.4})`);
          gradient.addColorStop(0.3, `${fade(0.75)}, ${opacity * 0.3})`);
          gradient.addColorStop(0.4, `${fade(0.66)}, ${opacity * 0.22})`);
          gradient.addColorStop(0.5, `${fade(0.56)}, ${opacity * 0.15})`);
          gradient.addColorStop(0.6, `${fade(0.47)}, ${opacity * 0.1})`);
          gradient.addColorStop(0.7, `${fade(0.38)}, ${opacity * 0.06})`);
          gradient.addColorStop(0.8, `${fade(0.28)}, ${opacity * 0.03})`);
          gradient.addColorStop(0.9, `${fade(0.19)}, ${opacity * 0.01})`);
          gradient.addColorStop(1, 'rgba(0, 0, 0, 0)');

          ctx.fillStyle = gradient;
          ctx.globalAlpha = 0.7;

          ctx.beginPath();
          ctx.arc(segment.x, segment.y, size, 0, Math.PI * 2);
          ctx.fill();
        }
      }
    }

    const trails: SmokeTrail[] = [];
    const trailCount = 8;

    for (let i = 0; i < trailCount; i++) {
      trails.push(new SmokeTrail(i));
    }

    let mouseX = canvas.width / 2;
    let mouseY = canvas.height / 2;
    let smoothMouseX = mouseX;
    let smoothMouseY = mouseY;

    const handleMouseMove = (e: MouseEvent) => {
      mouseX = e.clientX;
      mouseY = e.clientY;
    };

    document.addEventListener('mousemove', handleMouseMove);

    const animate = () => {
      smoothMouseX += (mouseX - smoothMouseX) * 0.2;
      smoothMouseY += (mouseY - smoothMouseY) * 0.2;

      ctx.clearRect(0, 0, canvas.width, canvas.height);
      ctx.globalCompositeOperation = 'screen';

      trails.forEach(trail => {
        trail.update(smoothMouseX, smoothMouseY);
        trail.draw();
      });

      ctx.globalCompositeOperation = 'source-over';
      animationId = requestAnimationFrame(animate);
    };

    animate();

    let autoAnimateAngle = 0;
    let lastMouseMove = Date.now();

    const handleMouseMoveTime = () => {
      lastMouseMove = Date.now();
    };

    document.addEventListener('mousemove', handleMouseMoveTime);

    const autoMoveInterval = setInterval(() => {
      if (Date.now() - lastMouseMove > 5000) {
        autoAnimateAngle += 0.005;
        mouseX = canvas.width / 2 + Math.cos(autoAnimateAngle) * 150;
        mouseY = canvas.height / 2 + Math.sin(autoAnimateAngle) * 150;
      }
    }, 50);

    return () => {
      window.removeEventListener('resize', resizeCanvas);
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mousemove', handleMouseMoveTime);
      clearInterval(autoMoveInterval);
      cancelAnimationFrame(animationId);
    };
  }, [isMobile, r, g, b]);

  return (
    <canvas
      ref={canvasRef}
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        pointerEvents: 'none',
        zIndex: 5
      }}
    />
  );
};
